import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Envelope, X } from "@phosphor-icons/react";

interface InitialResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
  onNavigateToMassmail: (content: string, subject: string) => void;
}

interface QuestionOption {
  id: string;
  label: string;
  requiresInput: boolean;
  inputPlaceholder?: string;
}

const QUESTION_OPTIONS: QuestionOption[] = [
  {
    id: "photos",
    label: "Extra foto's nodig van...",
    requiresInput: true,
    inputPlaceholder: "Bijvoorbeeld: het toestel, de waterschade, etc.",
  },
  {
    id: "serial",
    label: "Serienummer en model van toestel",
    requiresInput: false,
  },
  {
    id: "since_when",
    label: "Sinds wanneer doet het probleem zich voor",
    requiresInput: false,
  },
  {
    id: "other",
    label: "Andere vraag",
    requiresInput: true,
    inputPlaceholder: "Voer uw vraag in...",
  },
];

export const InitialResponseModal: React.FC<InitialResponseModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onNavigateToMassmail,
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [questionInputs, setQuestionInputs] = useState<Record<string, string>>(
    {},
  );

  // Get salutation from reporter
  const getSalutation = (reporter: any): string => {
    if (!reporter) return "Heer/Mevrouw";

    const firstName = reporter.firstName?.trim();
    const lastName = reporter.lastName?.trim();

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (lastName) {
      return `Heer/Mevrouw ${lastName}`;
    } else if (firstName) {
      return firstName;
    }

    return "Heer/Mevrouw";
  };

  const salutation = useMemo(
    () => getSalutation(ticket?.reporter),
    [ticket?.reporter],
  );

  // Generate email content based on selected questions (HTML formatted for MassMailPage)
  const generateEmailContent = (): string => {
    let content = `<p>Beste ${salutation},</p>`;
    content += `<p>Bedankt voor uw melding. Wij hebben deze goed ontvangen en doen het nodige om zo snel mogelijk te antwoorden.</p>`;

    if (selectedQuestions.size > 0) {
      content += `<p>Zodat wij u beter kunnen verder helpen, kan u nog even antwoorden op volgende vragen?</p>`;
      content += `<ul>`;

      selectedQuestions.forEach((questionId) => {
        switch (questionId) {
          case "photos":
            const photoSpec = questionInputs["photos"] || "[specificatie]";
            content += `<li>We hebben extra foto's nodig van <strong>${photoSpec}</strong>. Kan u deze bezorgen?</li>`;
            break;
          case "serial":
            content += `<li>Kan u ons het serienummer en model van het toestel bezorgen?</li>`;
            break;
          case "since_when":
            content += `<li>Sinds wanneer doet het probleem zich voor?</li>`;
            break;
          case "other":
            const otherQuestion = questionInputs["other"] || "[uw vraag]";
            content += `<li>${otherQuestion}</li>`;
            break;
        }
      });

      content += `</ul>`;
    } else {
      content += `<p>Alvast bedankt voor uw geduld.</p>`;
    }

    content += `<p>Met vriendelijke groeten,<br/>Ella</p>`;
    content += `<p style="font-size: 0.875em; color: #6B7280;"><strong>PS.</strong> Gelieve op deze e-mail te antwoorden en geen aparte e-mail te sturen.<br/>`;
    content += `Voor andere problemen kan u wel een aparte e-mail sturen naar <a href="mailto:helpdesk@yally.be" style="color: #2563EB;">helpdesk@yally.be</a></p>`;

    return content;
  };

  const emailContent = useMemo(
    () => generateEmailContent(),
    [selectedQuestions, questionInputs, salutation],
  );

  const handleToggleQuestion = (questionId: string) => {
    setSelectedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
        // Clear input when unchecking
        setQuestionInputs((inputs) => {
          const newInputs = { ...inputs };
          delete newInputs[questionId];
          return newInputs;
        });
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleInputChange = (questionId: string, value: string) => {
    setQuestionInputs((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSend = () => {
    try {
      const subject = `Ticket H${ticket.id} - ${ticket.title || "Informatie aanvraag"}`;
      onNavigateToMassmail(emailContent, subject);
      handleClose();
    } catch (error) {
      console.error("Error navigating to mass mail:", error);
    }
  };

  const handleClose = () => {
    setSelectedQuestions(new Set());
    setQuestionInputs({});
    onClose();
  };

  // Check if inputs are valid for selected questions that require them
  const isFormValid = useMemo(() => {
    for (const questionId of selectedQuestions) {
      const option = QUESTION_OPTIONS.find((q) => q.id === questionId);
      if (option?.requiresInput) {
        const inputValue = questionInputs[questionId]?.trim();
        if (!inputValue) {
          return false;
        }
      }
    }
    return true;
  }, [selectedQuestions, questionInputs]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-neutral-primary flex items-center gap-2">
            <Envelope size={24} className="text-moss-primary" />
            Initiële antwoord - Ticket H{ticket?.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Question Selection */}
          <div className="space-y-4">
            <h3 className="text-neutral-primary text-sm font-semibold">
              Selecteer de vragen die u wilt stellen:
            </h3>
            <div className="space-y-3">
              {QUESTION_OPTIONS.map((option) => (
                <div key={option.id} className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={option.id}
                      checked={selectedQuestions.has(option.id)}
                      onCheckedChange={() => handleToggleQuestion(option.id)}
                      className="mt-1"
                    />
                    <label
                      htmlFor={option.id}
                      className="text-neutral-primary flex-1 cursor-pointer text-sm font-medium select-none"
                    >
                      {option.label}
                    </label>
                  </div>
                  {option.requiresInput && selectedQuestions.has(option.id) && (
                    <div className="ml-8">
                      <Input
                        placeholder={option.inputPlaceholder}
                        value={questionInputs[option.id] || ""}
                        onChange={(e) =>
                          handleInputChange(option.id, e.target.value)
                        }
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Email Preview */}
          <div className="space-y-2">
            <h3 className="text-neutral-primary text-sm font-semibold">
              Voorbeeld e-mail:
            </h3>
            <div className="bg-muted border-border rounded-lg border p-4">
              <div className="border-border mb-3 border-b pb-3">
                <p className="text-muted-foreground text-xs">
                  <strong>Aan:</strong>{" "}
                  {ticket?.reporter?.email || "Geen e-mail beschikbaar"}
                </p>
                <p className="text-muted-foreground text-xs">
                  <strong>Onderwerp:</strong> Ticket H{ticket?.id} -{" "}
                  {ticket?.title || "Informatie aanvraag"}
                </p>
              </div>
              <div
                className="text-neutral-primary prose prose-sm max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: emailContent }}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="text-neutral-secondary-mid hover:text-neutral-primary"
          >
            <X size={16} className="mr-2" />
            Annuleer
          </Button>
          <Button
            onClick={handleSend}
            disabled={!isFormValid}
            className="bg-moss-primary hover:bg-moss-primary/90 text-white"
          >
            <Envelope size={16} className="mr-2" />
            Open in Mass Mail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
